import { useEffect } from 'react'

import { useFloorPlan } from '../../hooks/useFloorPlan'
import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import { Box as DarkBox, FeedItem, MapPinIcon } from '@hivewatch/dark'

import { CheckpointIProps } from '@/features/floor-plan'

import { DarkTourPlanHeader } from './DarkTourPlanHeader'
import {
  emptyTextStyles,
  rowIconTileStyles,
  rowListStyles,
  rowTitleStyles,
  skeletonRowStyles,
} from './DarkTourPlansPanel.styles'
import { DarkTourPlansSidePanel } from './DarkTourPlansSidePanel'

interface DarkTourPlanCheckPointsListIProps {
  onClose: () => void
  onCheckPointClick: (checkpoint: CheckpointIProps) => void
  onDeviceItemHoverIn: (deviceId: string) => void
  onEditClick: () => void
  isLoading: boolean
  name: string
  checkpoints: CheckpointIProps[]
  isExpanded: boolean
  onToggleExpansion: () => void
}

const LOADING_PLACEHOLDER_COUNT = 3

export const DarkTourPlanCheckPointsList = ({
  isLoading,
  name,
  checkpoints,
  isExpanded,
  onToggleExpansion,
  onClose,
  onEditClick,
  onCheckPointClick,
  onDeviceItemHoverIn,
}: DarkTourPlanCheckPointsListIProps) => {
  const colorScheme = useLightMode()
  const { t } = useTranslation()
  const { setHighlightedDeviceIds, setTourStopDeviceIds } = useFloorPlan()

  const handleOnClose = () => {
    onClose()
    setHighlightedDeviceIds(null)
    setTourStopDeviceIds(null)
  }

  const handleDeviceItemHoverOut = () => {
    const checkpointIds = checkpoints?.map((c) => c?.id)
    setHighlightedDeviceIds(checkpointIds)
  }

  useEffect(() => {
    if (checkpoints) {
      const checkpointIds = checkpoints?.map((c) => c?.id)
      setHighlightedDeviceIds(checkpointIds)
      setTourStopDeviceIds(checkpointIds)
    }
  }, [checkpoints])

  return (
    <DarkTourPlansSidePanel
      header={
        <DarkTourPlanHeader
          count={checkpoints?.length}
          isLoading={isLoading}
          name={name}
          onClose={handleOnClose}
          onEdit={onEditClick}
        />
      }
      isExpanded={isExpanded}
      onToggleExpansion={onToggleExpansion}
    >
      {isLoading ? (
        <DarkBox {...rowListStyles} {...skeletonRowStyles}>
          {Array.from({ length: LOADING_PLACEHOLDER_COUNT }, (_, index) => (
            <FeedItem
              colorScheme={colorScheme}
              icon={<MapPinIcon colorScheme={colorScheme} size={16} />}
              key={index}
              message={t('floors:plan_tour_checkpoints_loading')}
            />
          ))}
        </DarkBox>
      ) : checkpoints?.length > 0 ? (
        <DarkBox {...rowListStyles}>
          {checkpoints.map((checkpoint) => (
            <DarkBox
              key={checkpoint.id}
              onMouseEnter={() => onDeviceItemHoverIn?.(checkpoint.id)}
              onMouseLeave={handleDeviceItemHoverOut}
              w='full'
            >
              <FeedItem
                align='center'
                ariaLabel={checkpoint.name}
                colorScheme={colorScheme}
                icon={
                  <DarkBox {...rowIconTileStyles(colorScheme)}>
                    <MapPinIcon color='currentColor' size={16} />
                  </DarkBox>
                }
                isCompact
                message={
                  <DarkBox {...rowTitleStyles}>{checkpoint.name}</DarkBox>
                }
                onClick={() => onCheckPointClick(checkpoint)}
                timestamp={checkpoint.displayId}
              />
            </DarkBox>
          ))}
        </DarkBox>
      ) : (
        <DarkBox {...emptyTextStyles(colorScheme)}>
          {t('common:list_empty')}
        </DarkBox>
      )}
    </DarkTourPlansSidePanel>
  )
}
